import { CheckCheck, Eye, FileIcon, Download, Trash2, X } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { deleteMessageApi } from "../../app/api";
import toast from "react-hot-toast";
import { getMediaUrl, parseChatDate } from "./chatUtils";
 
export default function MessageBubble({ message, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isMe = message.sender === "me";
 
  const dateObj = parseChatDate(message.created_at || message.createdAt);
  const timeString = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
 
  if (message.type === "call_log") {
    return (
      <div className="my-4 flex justify-center">
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-4 py-1.5 shadow-sm backdrop-blur">
          <span className="text-[11px] font-medium tracking-wide text-white/60">{message.text}</span>
          <span className="text-[9px] font-bold uppercase tracking-tighter text-white/30">{timeString}</span>
        </div>
      </div>
    );
  }
 
 
  const handleDelete = async () => {
    try {
      const res = await deleteMessageApi(message.id);
      if (res.success) {
        toast.success("Message deleted");
        if (onDelete) onDelete(message.id);
      }
    } catch (err) {
      toast.error("Failed to delete message");
    }
    setShowMenu(false);
  };
 
  const renderText = (text) => {
    if (!text) return "";
    // Regex to match @Name or @Name Name
    const parts = text.split(/(@[a-zA-Z]+\s?[a-zA-Z]*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("@")) {
        return (
          <span key={i} className="text-yellow-300 font-bold bg-yellow-500/10 px-1 rounded">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const attachmentUrl = getAttachmentUrl(message.text);
  const attachmentName = getAttachmentName(message.text);
  const canPreviewFile = isPreviewableFile(attachmentName);
 
  return (
    <>
      <div className={`mb-1 flex ${isMe ? "justify-end" : "justify-start"}`}>
        <div
          className={`group relative flex max-w-[82%] flex-col rounded-[20px] px-3.5 py-2.5 text-[13px] leading-relaxed shadow-lg backdrop-blur ${
            isMe
              ? "rounded-br-md bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-violet-950/25"
              : "rounded-bl-md border border-white/10 bg-white/[0.07] text-white/90 shadow-black/15"
          }`}
          onContextMenu={(e) => {
            if (isMe && !message.is_deleted) {
              e.preventDefault();
              setShowMenu(true);
            }
          }}
        >
          {message.is_deleted ? (
            <span className="text-[11px] italic opacity-50">
              {isMe ? "You deleted this message" : "This message was deleted"}
            </span>
          ) : message.type === "image" ? (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="my-1 overflow-hidden rounded-2xl border border-white/10 text-left"
              aria-label={`Preview ${attachmentName}`}
            >
              <img
                src={attachmentUrl}
                alt={attachmentName}
                className="h-auto max-w-full cursor-pointer transition-opacity hover:opacity-90"
              />
            </button>
          ) : message.type === "file" ? (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="my-1 flex w-full items-center gap-2 rounded-2xl border border-white/10 bg-black/20 p-2 text-left transition-colors hover:bg-black/30"
            >
              <div className="rounded-xl bg-indigo-500/20 p-2">
                <FileIcon size={20} className="text-indigo-400" />
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="truncate text-[12px] font-medium">
                  {attachmentName}
                </p>
                <p className="text-[10px] text-white/40">Click to preview</p>
              </div>
              <Download size={16} className="text-white/40" />
            </button>
          ) : (
            <span className="break-words">{renderText(message.text)}</span>
          )}
 
          {showMenu && (
            <div className="absolute top-0 right-0 mt-8 mr-2 z-50 bg-[#12141c] border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 min-w-[120px]">
              <button
                onClick={handleDelete}
                className="w-full text-left px-4 py-2.5 text-[11px] font-bold text-red-400 hover:bg-red-500/10 flex items-center justify-between group/btn"
                type="button"
              >
                DELETE
                <Trash2 size={14} className="group-hover/btn:scale-110 transition-transform" />
              </button>
              <button
                onClick={() => setShowMenu(false)}
                className="w-full text-left px-4 py-1.5 text-[9px] text-white/20 hover:text-white/40 text-center uppercase font-black"
                type="button"
              >
                Cancel
              </button>
            </div>
          )}
        
          <div className={`mt-1.5 flex items-center justify-end gap-1 ${isMe ? "text-white/55" : "text-white/35"}`}>
            <span className="text-[9px] font-bold">{timeString}</span>
            {isMe && !message.group_id && !message.groupId && (
              message.is_seen ? (
                <span className="inline-flex items-center gap-0.5 text-sky-300" title="Seen">
                  <Eye size={11} />
                </span>
              ) : (
                <span className="inline-flex items-center gap-0.5 text-white/65" title="Delivered">
                  <CheckCheck size={11} />
                </span>
              )
            )}
          </div>
        </div>
      </div>

      {previewOpen && createPortal(
        <AttachmentPreviewModal
          url={attachmentUrl}
          name={attachmentName}
          type={message.type}
          canPreviewFile={canPreviewFile}
          onClose={() => setPreviewOpen(false)}
        />,
        document.body
      )}
    </>
  );
}

function getAttachmentUrl(path = "") {
  return getMediaUrl(path);
}

function getAttachmentName(path = "") {
  const cleanPath = String(path).split("?")[0];
  return cleanPath.split("/").pop() || "Attachment";
}

function isPreviewableFile(fileName = "") {
  return /\.(pdf|txt|csv|json|html?)$/i.test(fileName);
}

function AttachmentPreviewModal({ url, name, type, canPreviewFile, onClose }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-white/15 bg-[#11101f] shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{name}</p>
            <p className="text-xs text-white/45">Attachment preview</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={url}
              download={name}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/15"
            >
              <Download size={15} />
              Download
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-white/10 p-2 text-white transition hover:bg-white/15"
              aria-label="Close attachment preview"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex min-h-[320px] flex-1 items-center justify-center overflow-auto bg-black/25 p-4">
          {type === "image" ? (
            <img src={url} alt={name} className="max-h-[78vh] max-w-full rounded-2xl object-contain" />
          ) : canPreviewFile ? (
            <iframe title={name} src={url} className="h-[78vh] w-full rounded-2xl border border-white/10 bg-white" />
          ) : (
            <div className="max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-500/20 text-indigo-200">
                <FileIcon size={26} />
              </div>
              <p className="text-sm font-semibold text-white">Preview not available</p>
              <p className="mt-2 text-xs text-white/50">
                This attachment type cannot be previewed in the chat. Use Download to save it.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}




// import { Check, CheckCheck, FileIcon, Download, Trash2 } from "lucide-react";
// import { useState } from "react";
// import { deleteMessageApi } from "../../app/api";
// import toast from "react-hot-toast";

// export default function MessageBubble({ message, onDelete }) {
//   const [showMenu, setShowMenu] = useState(false);
//   const isMe = message.sender === "me";

//   let dateObj;
//   const rawDate = message.created_at || message.createdAt || Date.now();
//   if (typeof rawDate === 'string' && !rawDate.includes('Z')) {
//     dateObj = new Date(rawDate.replace(' ', 'T') + 'Z');
//   } else {
//     dateObj = new Date(rawDate);
//   }
//   const timeString = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

//   if (message.type === "call_log") {
//     return (
//       <div className="flex justify-center my-3">
//         <div className="bg-white/5 border border-white/10 rounded-full px-4 py-1 flex items-center gap-2 shadow-sm">
//           <span className="text-[11px] text-white/60 font-medium tracking-wide">{message.text}</span>
//           <span className="text-[9px] text-white/30 font-bold uppercase tracking-tighter">{timeString}</span>
//         </div>
//       </div>
//     );
//   }


//   const handleDelete = async () => {
//     try {
//       const res = await deleteMessageApi(message.id);
//       if (res.success) {
//         toast.success("Message deleted");
//         if (onDelete) onDelete(message.id);
//       }
//     } catch (err) {
//       toast.error("Failed to delete message");
//     }
//     setShowMenu(false);
//   };

//   const renderText = (text) => {
//     if (!text) return "";
//     // Regex to match @Name or @Name Name
//     const parts = text.split(/(@[a-zA-Z]+\s?[a-zA-Z]*)/g);
//     return parts.map((part, i) => {
//       if (part.startsWith("@")) {
//         return (
//           <span key={i} className="text-yellow-300 font-bold bg-yellow-500/10 px-1 rounded">
//             {part}
//           </span>
//         );
//       }
//       return part;
//     });
//   };

//   return (
//     <div className={`flex ${isMe ? "justify-end" : "justify-start"} mb-1`}>
//       <div
//         className={`flex flex-col relative px-3 py-1.5 rounded-2xl text-[13px] max-w-[80%] group ${isMe ? "bg-indigo-600 text-white rounded-tr-none shadow-lg shadow-indigo-500/10" : "bg-[#1f2228] text-white/90 rounded-tl-none border border-white/5"}`}
//         onContextMenu={(e) => {
//           if (isMe && !message.is_deleted) {
//             e.preventDefault();
//             setShowMenu(true);
//           }
//         }}
//       >
//         {message.is_deleted ? (
//           <span className="italic opacity-50 text-[11px]">
//             {isMe ? "You deleted this message" : "This message was deleted"}
//           </span>
//         ) : message.type === "image" ? (
//           <div className="rounded-lg overflow-hidden my-1">
//             <img 
//               src={message.text.startsWith('http') ? message.text : `/${message.text.startsWith('/') ? message.text.slice(1) : message.text}`} 
//               alt="Content" 
//               className="max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
//               onClick={() => window.open(message.text.startsWith('http') ? message.text : `/${message.text.startsWith('/') ? message.text.slice(1) : message.text}`, '_blank')}
//             />
//           </div>
//         ) : message.type === "file" ? (
//           <a 
//             href={message.text.startsWith('http') ? message.text : `/${message.text.startsWith('/') ? message.text.slice(1) : message.text}`}
//             target="_blank"
//             rel="noopener noreferrer"
//             className="flex items-center gap-2 bg-black/20 p-2 rounded-lg hover:bg-black/30 transition-colors my-1"
//           >
//             <div className="p-2 bg-indigo-500/20 rounded-md">
//               <FileIcon size={20} className="text-indigo-400" />
//             </div>
//             <div className="flex-1 overflow-hidden">
//               <p className="text-[12px] font-medium truncate">
//                 {message.text.split('/').pop()}
//               </p>
//               <p className="text-[10px] text-white/40">Click to download</p>
//             </div>
//             <Download size={16} className="text-white/40" />
//           </a>
//         ) : (
//           <span>{renderText(message.text)}</span>
//         )}

//         {showMenu && (
//           <div className="absolute top-0 right-0 mt-8 mr-2 z-50 bg-[#12141c] border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 min-w-[120px]">
//             <button
//               onClick={handleDelete}
//               className="w-full text-left px-4 py-2.5 text-[11px] font-bold text-red-400 hover:bg-red-500/10 flex items-center justify-between group/btn"
//               type="button"
//             >
//               DELETE
//               <Trash2 size={14} className="group-hover/btn:scale-110 transition-transform" />
//             </button>
//             <button
//               onClick={() => setShowMenu(false)}
//               className="w-full text-left px-4 py-1.5 text-[9px] text-white/20 hover:text-white/40 text-center uppercase font-black"
//               type="button"
//             >
//               Cancel
//             </button>
//           </div>
//         )}
        
//         <div className="flex justify-end items-center gap-1 mt-1 opacity-40">
//           <span className="text-[9px] font-bold">{timeString}</span>
//           {isMe && !message.group_id && !message.groupId && (
//             message.is_seen ? <CheckCheck size={10} className="text-blue-400" /> : <Check size={10} />
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }
